import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, eur, gr, reportUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FilePdf, Prohibit, ArrowUUpLeft, PaperPlaneTilt, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = {
  invoice: "Τιμολόγιο",
  receipt: "Απόδειξη",
  credit_invoice: "Πιστωτικό τιμολόγιο",
  credit_receipt: "Πιστωτικό λιανικής",
};

const MyDataBadge = ({ doc }) => {
  if (doc.mydata_mark) return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">ΜΑΡΚ {doc.mydata_mark}</Badge>;
  if (doc.mydata_status === "error") return <Badge className="bg-red-100 text-red-800 hover:bg-red-100" title={doc.mydata_error}>Σφάλμα myDATA</Badge>;
  return <Badge variant="secondary">Χωρίς αποστολή</Badge>;
};

export default function FiscalBook() {
  const qc = useQueryClient();
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [credit, setCredit] = useState(null);
  const [cancelDoc, setCancelDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["fiscal"],
    queryFn: async () => (await api.get("/fiscal")).data,
  });

  const { data: pending = [] } = useQuery({
    queryKey: ["fiscal-pending"],
    queryFn: async () => (await api.get("/fiscal/pending")).data,
  });

  const [issue, setIssue] = useState(null);

  const doIssue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/fiscal/issue", {
        boat_id: issue.boat_id,
        doc_type: issue.doc_type,
        amount: Number(issue.amount || 0),
        note: issue.note || null,
        period_end: issue.end_date || null,
      });
      await qc.invalidateQueries({ queryKey: ["fiscal"] });
      await qc.invalidateQueries({ queryKey: ["fiscal-pending"] });
      await qc.invalidateQueries({ queryKey: ["alerts"] });
      window.open(reportUrl(`/fiscal/${data.id}.pdf`), "_blank");
      if (data.mydata_status === "success") toast.success(`${data.series}-${data.aa} · ΜΑΡΚ ${data.mydata_mark}`);
      else if (data.mydata_status === "error") toast.warning(`Εκδόθηκε ${data.series}-${data.aa}, αποστολή myDATA απέτυχε: ${data.mydata_error || ""}`);
      else toast.success(`Εκδόθηκε ${data.series}-${data.aa}`);
      setIssue(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα έκδοσης παραστατικού");
    }
    setBusy(false);
  };

  const rows = docs.filter((d) => {
    if (type !== "all" && d.doc_type !== type) return false;
    const s = `${d.series}-${d.aa} ${d.owner || ""} ${d.boat_name || ""}`.toLowerCase();
    return !q || s.includes(q.toLowerCase());
  });

  const totals = rows.reduce(
    (a, d) => {
      const sign = d.doc_type.startsWith("credit") || d.status === "cancelled" ? 0 : 1;
      const credited = d.doc_type.startsWith("credit") ? d.gross : 0;
      return { net: a.net + sign * d.net, vat: a.vat + sign * d.vat, gross: a.gross + sign * d.gross, credited: a.credited + credited };
    },
    { net: 0, vat: 0, gross: 0, credited: 0 }
  );

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/fiscal/${cancelDoc.id}/cancel`, null, { params: { reason: cancelDoc.reason || "" } });
      await qc.invalidateQueries({ queryKey: ["fiscal"] });
      await qc.invalidateQueries({ queryKey: ["fiscal-pending"] });
      await qc.invalidateQueries({ queryKey: ["alerts"] });
      toast.success(`Ακυρώθηκε το ${cancelDoc.series}-${cancelDoc.aa}`);
      setCancelDoc(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα ακύρωσης");
    }
    setBusy(false);
  };

  const doCredit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/fiscal/${credit.id}/credit`, {
        amount: credit.amount ? Number(credit.amount) : null,
        note: credit.note || null,
      });
      await qc.invalidateQueries({ queryKey: ["fiscal"] });
      window.open(reportUrl(`/fiscal/${data.id}.pdf`), "_blank");
      if (data.mydata_status === "success") toast.success(`Πιστωτικό ${data.series}-${data.aa} · ΜΑΡΚ ${data.mydata_mark}`);
      else if (data.mydata_status === "error") toast.warning(`Εκδόθηκε ${data.series}-${data.aa}, αποστολή myDATA απέτυχε: ${data.mydata_error || ""}`);
      else toast.success(`Εκδόθηκε πιστωτικό ${data.series}-${data.aa}`);
      setCredit(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα έκδοσης πιστωτικού");
    }
    setBusy(false);
  };

  const resend = async (d) => {
    try {
      const { data } = await api.post(`/fiscal/${d.id}/resend`);
      await qc.invalidateQueries({ queryKey: ["fiscal"] });
      if (data.mydata_status === "success") toast.success(`Στάλθηκε · ΜΑΡΚ ${data.mydata_mark}`);
      else toast.warning(data.mydata_error || "Η αποστολή απέτυχε");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα αποστολής");
    }
  };

  return (
    <div className="space-y-6" data-testid="fiscal-book-page">
      <div>
        <h1 className="font-head text-2xl font-bold text-[#142C4A]">Βιβλίο παραστατικών</h1>
        <p className="mt-1 text-sm text-muted-foreground">Όλα τα τιμολόγια, οι αποδείξεις και τα πιστωτικά, με την κατάσταση αποστολής στο myDATA.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        {[
          ["Καθαρή αξία", totals.net],
          ["Φ.Π.Α. 24%", totals.vat],
          ["Συνολική αξία", totals.gross],
          ["Πιστώσεις", totals.credited],
        ].map(([label, value]) => (
          <Card key={label} className="rounded-sm border-border p-4 shadow-none" data-testid={`fiscal-total-${label}`}>
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="mt-1 font-head text-lg font-bold text-[#142C4A]">{eur(value)}</div>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden rounded-sm border-border shadow-none" data-testid="pending-issues-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="font-head font-bold text-[#142C4A]">Θέσεις σε χρήση — έκδοση παραστατικού</h2>
            <p className="text-xs text-muted-foreground">Χρονολογικά κατά ημερομηνία εισόδου. Όπου υπάρχει σύμβαση, το ποσό συμπληρώνεται αυτόματα από αυτήν.</p>
          </div>
          <span className="text-xs text-muted-foreground" data-testid="pending-count">{pending.filter((p) => !p.issued).length} εκκρεμείς</span>
        </div>
        {pending.length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground" data-testid="pending-empty">Δεν υπάρχουν θέσεις σε χρήση.</div>
        ) : (
          <div className="divide-y divide-border">
            {pending.map((p) => (
              <div
                key={p.boat_id}
                className="grid grid-cols-1 items-center gap-3 px-5 py-3 sm:grid-cols-[minmax(0,1fr)_140px_150px_190px]"
                data-testid={`pending-issue-${p.boat_id}`}
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {p.name} <span className="text-muted-foreground">· θέση {p.slot_label || p.slot_number}</span>
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {p.owner} · {gr(p.start_date)} - {gr(p.end_date)}
                  </div>
                </div>
                <div className="text-sm sm:text-right">
                  <div className="font-medium">{eur(p.suggested_amount)}</div>
                  <div className="text-xs text-muted-foreground">{p.has_contract ? "τιμή σύμβασης" : "ορίζεται από τον διαχειριστή"}</div>
                </div>
                <div className="sm:justify-self-center">
                  {p.issued ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`pending-issued-${p.boat_id}`}>
                      Εκδόθηκε {p.issued.ref}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Εκκρεμεί</Badge>
                  )}
                </div>
                <Button
                  size="sm"
                  className="w-full rounded-sm bg-[#0055FF] hover:bg-[#0044CC] sm:justify-self-end"
                  onClick={() => setIssue({ ...p, doc_type: "invoice", amount: p.has_contract ? String(p.contract_amount) : "", note: "" })}
                  data-testid={`pending-issue-btn-${p.boat_id}`}
                >
                  <Receipt size={14} weight="bold" /> Έκδοση παραστατικού
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-48">
          <Label className="text-xs text-muted-foreground">Τύπος</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="mt-1 rounded-sm" data-testid="fiscal-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Όλα</SelectItem>
              {Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-64">
          <Label className="text-xs text-muted-foreground">Αναζήτηση</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="αριθμός, πελάτης, σκάφος" className="mt-1 rounded-sm" data-testid="fiscal-search-input" />
        </div>
      </div>

      <Card className="overflow-hidden rounded-sm border-border shadow-none">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Φόρτωση...</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground" data-testid="fiscal-empty">Δεν έχει εκδοθεί κανένα παραστατικό.</div>
        ) : (
          <div className="divide-y divide-border">
            {rows.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`fiscal-row-${d.id}`}>
                <div className="min-w-[240px]">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span>{d.series}-{d.aa}</span>
                    <span className="text-muted-foreground">· {TYPES[d.doc_type]}</span>
                    {d.status === "cancelled" && <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Ακυρωμένο</Badge>}
                    {d.credited_by && <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100">Πιστωμένο</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {gr(d.doc_date)} · {d.owner || "—"} · {d.boat_name || "—"}
                    {d.original_ref && ` · σε συσχέτιση με ${d.original_ref}`}
                  </div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-medium">{eur(d.gross)}</div>
                  <div className="text-xs text-muted-foreground">καθαρή {eur(d.net)} · ΦΠΑ {eur(d.vat)}</div>
                </div>
                <MyDataBadge doc={d} />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => window.open(reportUrl(`/fiscal/${d.id}.pdf`), "_blank")} data-testid={`fiscal-pdf-${d.id}`}>
                    <FilePdf size={14} weight="bold" /> PDF
                  </Button>
                  {!d.mydata_mark && d.mydata_status === "error" && d.status !== "cancelled" && (
                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => resend(d)} data-testid={`fiscal-resend-${d.id}`}>
                      <PaperPlaneTilt size={14} weight="bold" /> myDATA
                    </Button>
                  )}
                  {!d.doc_type.startsWith("credit") && d.status !== "cancelled" && !d.credited_by && (
                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setCredit({ ...d, amount: "", note: "" })} data-testid={`fiscal-credit-${d.id}`}>
                      <ArrowUUpLeft size={14} weight="bold" /> Πιστωτικό
                    </Button>
                  )}
                  {d.status !== "cancelled" && !d.credited_by && (
                    <Button size="sm" variant="outline" className="rounded-sm text-red-700 hover:bg-red-50" onClick={() => setCancelDoc({ ...d, reason: "" })} data-testid={`fiscal-cancel-${d.id}`}>
                      <Prohibit size={14} weight="bold" /> Ακύρωση
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={!!issue} onOpenChange={(o) => !o && setIssue(null)}>
        <DialogContent data-testid="pending-issue-dialog">
          <DialogHeader><DialogTitle>Έκδοση παραστατικού — {issue?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-sm bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              Θέση {issue?.slot_label || issue?.slot_number} · {issue?.owner} · περίοδος {gr(issue?.start_date)} - {gr(issue?.end_date)}
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Τύπος παραστατικού</Label>
              <Select value={issue?.doc_type} onValueChange={(v) => setIssue({ ...issue, doc_type: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="pending-doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Τιμολόγιο</SelectItem>
                  <SelectItem value="receipt">Απόδειξη</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">
                Συνολικό ποσό με ΦΠΑ (€) {issue?.has_contract && <span className="text-[#0F766E]">· από σύμβαση</span>}
              </Label>
              <Input type="number" step="0.01" value={issue?.amount || ""} onChange={(e) => setIssue({ ...issue, amount: e.target.value })} className="mt-1 rounded-sm" data-testid="pending-amount-input" />
              <div className="mt-1 text-[11px] text-muted-foreground" data-testid="pending-vat-hint">
                Καθαρή αξία {(Number(issue?.amount || 0) / 1.24).toFixed(2)} € · ΦΠΑ 24% {(Number(issue?.amount || 0) - Number(issue?.amount || 0) / 1.24).toFixed(2)} €
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Αιτιολογία</Label>
              <Input value={issue?.note || ""} onChange={(e) => setIssue({ ...issue, note: e.target.value })} className="mt-1 rounded-sm" data-testid="pending-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setIssue(null)}>Άκυρο</Button>
            <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" disabled={busy} onClick={doIssue} data-testid="pending-issue-submit">Έκδοση</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!credit} onOpenChange={(o) => !o && setCredit(null)}>
        <DialogContent data-testid="fiscal-credit-dialog">
          <DialogHeader><DialogTitle>Έκδοση πιστωτικού για {credit?.series}-{credit?.aa}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Ποσό με ΦΠΑ (κενό = πλήρης πίστωση {eur(credit?.gross || 0)})</Label>
              <Input type="number" step="0.01" value={credit?.amount || ""} onChange={(e) => setCredit({ ...credit, amount: e.target.value })} className="mt-1 rounded-sm" data-testid="fiscal-credit-amount" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Αιτιολογία</Label>
              <Input value={credit?.note || ""} onChange={(e) => setCredit({ ...credit, note: e.target.value })} placeholder="π.χ. επιστροφή, έκπτωση, λάθος χρέωση" className="mt-1 rounded-sm" data-testid="fiscal-credit-note" />
            </div>
            {credit?.doc_type === "invoice" && !credit?.mydata_mark && (
              <p className="text-xs text-amber-700">Το αρχικό τιμολόγιο δεν έχει ΜΑΡΚ ΑΑΔΕ, οπότε το πιστωτικό δεν θα σταλεί στο myDATA.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setCredit(null)}>Άκυρο</Button>
            <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" disabled={busy} onClick={doCredit} data-testid="fiscal-credit-submit">Έκδοση πιστωτικού</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelDoc} onOpenChange={(o) => !o && setCancelDoc(null)}>
        <DialogContent data-testid="fiscal-cancel-dialog">
          <DialogHeader><DialogTitle>Ακύρωση {cancelDoc?.series}-{cancelDoc?.aa}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Η ακύρωση είναι οριστική. {cancelDoc?.mydata_mark ? "Το παραστατικό θα ακυρωθεί και στο myDATA." : "Το παραστατικό δεν έχει σταλεί στο myDATA."}
              {" "}Αν η υπηρεσία πράγματι παρασχέθηκε, εκδώστε πιστωτικό αντί ακύρωσης.
            </p>
            <div>
              <Label className="text-xs text-muted-foreground">Αιτία ακύρωσης</Label>
              <Input value={cancelDoc?.reason || ""} onChange={(e) => setCancelDoc({ ...cancelDoc, reason: e.target.value })} className="mt-1 rounded-sm" data-testid="fiscal-cancel-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setCancelDoc(null)}>Άκυρο</Button>
            <Button className="rounded-sm bg-red-600 hover:bg-red-700" disabled={busy} onClick={doCancel} data-testid="fiscal-cancel-submit">Ακύρωση παραστατικού</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
